<template>
	<div class="interactive-pie-chart" ref="chartContainer">
		<div>
			<svg :width="responsiveSize" :height="responsiveSize" class="pie-chart">
				<g :transform="`translate(${responsiveSize / 2}, ${responsiveSize / 2})`">
					<path
						v-for="(segment, index) in chartSegments"
						:key="index"
						:class="{ 'chart-segment': true, 'selected': segment.isSelected }"
						:d="segment.path"
						:fill="segment.color"
						stroke="#fff"
						:stroke-width="1"
						:opacity="1"
						@click="toggleSegment(index)"
					/>
					
					<text
						v-for="(segment, index) in chartSegments"
						:key="`label-${index}`"
						:class="{ 'sector-label': true, 'selected': segment.isSelected }"
						:x="segment.labelX"
						:y="segment.labelY"
						text-anchor="middle"
						v-show="segment.value > 0"
					>
						{{ segment.value }}
					</text>
					
					<circle
						cx="0" cy="0" r="25"
						fill="white" stroke="#666" stroke-width="1"
					/>
					<text x="0" y="8" text-anchor="middle" class="total-value">{{ totalValue }}</text>

					<text v-if="totalValue === 0" x="0" y="50" text-anchor="middle" class="no-data-text">нет данных</text>
				</g>
			</svg>
		</div>
		<div class="legend">
			<div
				v-for="(segment, index) in chartSegments"
				:key="index"
				:class="{ 'legend-item': true, 'selected': segment.isSelected }"
				@click="toggleSegment(index)"
			>
				<div class="legend-color" :style="{ backgroundColor: segment.color }" />
				<span class="legend-label">{{ segment.label }}</span>
				<span class="legend-value">{{ segment.value }}</span>
			</div>
		</div>
	</div>
</template>

<script>
import { computed, onMounted, ref, watch, toRef } from 'vue';

import { useElementSize, useVModel } from '@vueuse/core';

import Consts from '@/api/Consts';


export default {
	props: {
		selectedIndices: {
			type: Array,
			default: () => [],
		},
		data: {
			type: Array,
			required: true,
			validator: value => {
				return value.every(item => 
					typeof item.label === 'string' && 
					typeof item.value === 'number' && 
					item.value >= 0
				)
			}
		},
		size: {
			type: Number,
			default: 500
		}
	},
	setup(props, { emit }) {
		const selectedIndicesRef = useVModel(props, 'selectedIndices', emit);
		const chartContainer = ref(null);
		const dataRef = toRef(props, 'data');

		const responsiveSize = ref(props.size);

		onMounted(() => {
			const { width } = useElementSize(chartContainer.value);
			watch(width, (newWidth) => {
				responsiveSize.value = Math.min(newWidth, props.size);
			});
		});

		const chartRadius = computed(() => responsiveSize.value * 0.4);
		const totalValue = computed(() => dataRef.value.reduce((sum, item) => sum + item.value, 0));

		const chartSegments = computed(() => {
			let currentAngle = -Math.PI / 2;
			if (totalValue.value === 0) {
				return [];
			}
			return dataRef.value.map((item, index) => {
				const angle = (item.value / totalValue.value) * 2 * Math.PI;
				const startAngle = currentAngle;
				const endAngle = currentAngle + angle;
				const midAngle = startAngle + angle / 2;
				
				const segmentRadius = chartRadius.value + (selectedIndicesRef.value.includes(index) ? 10 : 0);
				
				const x1 = segmentRadius * Math.cos(startAngle);
				const y1 = segmentRadius * Math.sin(startAngle);
				const x2 = segmentRadius * Math.cos(endAngle);
				const y2 = segmentRadius * Math.sin(endAngle);
				
				const largeArcFlag = angle > Math.PI ? 1 : 0;
				
				const path = [
					'M 0 0',
					`L ${x1} ${y1}`,
					`A ${segmentRadius} ${segmentRadius} 0 ${largeArcFlag} 1 ${x2} ${y2}`,
					'Z'
				].join(' ');
				
				const labelRadius = segmentRadius * 0.65;
				const labelX = labelRadius * Math.cos(midAngle);
				const labelY = labelRadius * Math.sin(midAngle);
				
				currentAngle = endAngle;
				
				return {
					...item,
					path,
					color: getSegmentColor(index),
					isSelected: selectedIndicesRef.value.includes(index),
					labelX,
					labelY,
				};
			});
		});


		const getSegmentColor = (index) => {
			if (dataRef.value[index].color) {
				return dataRef.value[index].color;
			}
			return '#' + Consts.CHART_COLORS[index % Consts.CHART_COLORS.length];
		};

		const toggleSegment = (index) => {
			if (selectedIndicesRef.value.includes(index)) {
				selectedIndicesRef.value = selectedIndicesRef.value.filter(i => i !== index);
			} else {
				selectedIndicesRef.value.push(index);
			}
		};

		return {
			chartContainer,
			responsiveSize,
			totalValue,
			chartRadius,
			chartSegments,
			getSegmentColor,
			toggleSegment,
		};
	}
};
</script>

<style scoped>
.interactive-pie-chart {
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 16px;
	font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, 'Noto Sans', sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol', 'Noto Color Emoji';
	font-size: 14px;
}

.pie-chart {
	display: block;
}

.chart-segment {
	cursor: pointer;
	transition: all 0.2s ease;
}

.chart-segment:hover {
	opacity: 0.8 !important;
}

.chart-segment.selected {
	filter: drop-shadow(0 0 8px rgba(0, 0, 0, 0.3));
}

.sector-label {
	font-size: 18px;
	fill: #333;
	pointer-events: none;
	transition: all 0.2s ease;
}

.sector-label.selected {
	font-size: 18px;
	fill: #000;
	font-weight: 600;
}

.total-value {
	font-size: 24px;
	fill: #000;
}

.legend {
	display: flex;
	flex-wrap: wrap;
	gap: 8px;
	justify-content: flex-start;
}

.legend-item {
	display: flex;
	align-items: center;
	gap: 8px;
	padding: 4px 8px;
	border-radius: 4px;
	cursor: pointer;
	transition: all 0.2s ease;
	border: 1px solid transparent;
}

.legend-item:hover {
	background-color: #f0f0f0;
}

.legend-item.selected {
	background-color: #e3f2fd;
	border-color: #1890ff;
}

.legend-color {
	width: 16px;
	height: 16px;
	flex-shrink: 0;
}

.legend-label {
	color: #333;
}

.legend-value {
	font-weight: bold;
	color: #666;
}

.no-data-text {
	font-size: 14px;
	color: black;
	opacity: 0.5;
}
</style>
